import React, { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getSandreLabel } from '../../../../utils/StringUtil'
import { getYear } from '../../../../utils/DateUtil'
import { SANDRE } from '../../../../referencial/constants/ReferencialConstants'
import Input from '../../../../components/forms/Input'
import Select from '../../../../components/forms/Select'
import Textarea from '../../../../components/forms/Textarea'
import InstallationAction from '../../../actions/InstallationAction'
import { max, orderBy } from 'lodash'
import { Card, CardContent, Grid } from '@mui/material'
import useSandreList from 'utils/customHook/useSandreList'
import DtoUserBookmark from 'administration/components/user/dto/DtoUserBookmark'
import { getLogin } from 'utils/SettingUtils'
import UserAction from 'administration/components/user/actions/UserAction'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { CardTitle } from 'components/card/NewCard'
import { favOrange } from 'utils/constants/ColorTheme'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { hasValue } from 'utils/NumberUtil'

const InstallationInfosDashboard = () => {
    const dispatch = useDispatch()

    const {
        installation,
        sandreCodes,
        installationsTypes,
        contributors,
        industrialSite,
        userBookmarks,
    } = useSelector(store => ({
        installation: store.InstallationReducer.installation,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        installationsTypes: store.InstallationReducer.installationsTypes,
        contributors: store.ContributorReducer.contributors,
        industrialSite: store.InstallationReducer.industrialSite,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    useEffect(() => {
        if (!installationsTypes.length) {
            dispatch(InstallationAction.fetchInstallationTypes())
        }
    }, [])

    const states = useSandreList(SANDRE.CODE_ETAT)

    const getContributors = () => {
        const { link_contributors: stationContributors } = installation
        if (stationContributors?.length) {
            const maxStartDate = (max(stationContributors.map(sc => sc.startDate)))
            const space = <span className='padding-left-2' />
            const data = orderBy(stationContributors, 'startDate').map(c => {
                const contributor = contributors.find(cs => cs.id === c.idContributor)
                const type = getSandreLabel(sandreCodes, SANDRE.CONTRIBUTOR_TYPES, c.contributorType)
                return contributor && (
                    <Grid sx={{ fontSize: '14px' }}>{getYear(c.startDate || maxStartDate)} {space} {contributor.mnemonique || contributor.name} {space} {type} {space} {c.internalReference || null}</Grid>
                ) || <Grid sx={{ fontSize: '14px' }}>{c.startDate && getYear(c.startDate) || ''} {c.startDate && space} {c.contributorType && type || ''} {c.contributorType && space} {c.internalReference || ''}</Grid>
            })
            return (
                <Grid>
                    <Grid sx={{ fontWeight: 'bold', fontSize: '14px' }}>{`${i18n.contributors}:`}</Grid>
                    {data}
                </Grid>
            )
        }
        return null
    }

    const isBookmark = userBookmarks.some(ub => ub.stationType === STATION_TYPE_CONSTANT.INSTALLATION && ub.identifiant === installation.code)

    const setBookmark = () => {
        const funcBookmark = isBookmark ? UserAction.deleteBookmark : UserAction.createBookmark
        dispatch(funcBookmark(new DtoUserBookmark({ login: getLogin(), identifiant: installation.code, stationType: STATION_TYPE_CONSTANT.INSTALLATION })))
    }

    const actions = [{
        icon: isBookmark ? 'star' : 'star_border',
        color: favOrange,
        onClick: setBookmark,
    }]

    return (
        <Card>
            <CardTitle title={i18n.generalInfos} actions={actions}/>
            <CardContent>
                <Grid container alignItems='flex-start'>
                    <Grid container size={8}>
                        <Grid size={6}>
                            <Select
                                value={installation.installationType}
                                label={i18n.installationType}
                                options={installationsTypes}
                                readMode
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                value={installation.code}
                                title={i18n.code}
                                disabled
                                readMode
                            />
                        </Grid>
                        {
                            installation.name && (
                                <Grid size={6}>
                                    <Input
                                        value={installation.name}
                                        title={i18n.name}
                                        disabled
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            industrialSite.siret && (
                                <Grid size={6}>
                                    <Input
                                        value={industrialSite.siret}
                                        title={i18n.siret}
                                        disabled
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            installation.creationDate && (
                                <Grid size={6}>
                                    <SimpleDatePicker
                                        value={installation.creationDate}
                                        id='creationDate'
                                        label={i18n.creationDate}
                                        endDate={installation.endDate}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            installation.declarationNumber && (
                                <Grid size={6}>
                                    <Input
                                        value={installation.declarationNumber}
                                        title={i18n.declarationNumber}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            installation.id && (
                                <Grid size={6}>
                                    <Input
                                        value={installation.id}
                                        title={i18n.internalIdentifier}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            installation.mnemonic && (
                                <Grid size={6}>
                                    <Input
                                        value={installation.mnemonic}
                                        title={i18n.mnemonic}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            hasValue(installation.stateCode) && (
                                <Grid size={6}>
                                    <Select
                                        value={installation.stateCode}
                                        label={i18n.state}
                                        options={states}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                        {
                            installation.descriptive && (
                                <Grid size={12}>
                                    <Textarea
                                        value={installation.descriptive}
                                        title={i18n.descriptive}
                                        readMode
                                    />
                                </Grid>
                            )
                        }
                    </Grid>
                    <Grid size={4}>
                        {getContributors(installation)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

InstallationInfosDashboard.propTypes = {
}

export default InstallationInfosDashboard